import { commonAPI } from "@/api/commonAPI.js";

const MANAGED_ATTR = "data-common-meta-tag";
let latestRequestId = 0;

const getMetaRows = (response) => {
    const data = response?.data;

    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.data?.list)) return data.data.list;
    if (Array.isArray(data?.data?.result)) return data.data.result;
    if (Array.isArray(data?.data?.resultList)) return data.data.resultList;
    if (Array.isArray(data?.list)) return data.list;
    if (Array.isArray(data?.result)) return data.result;
    if (Array.isArray(data?.resultList)) return data.resultList;
    if (Array.isArray(data?.resultData)) return data.resultData;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data?.items)) return data.items;

    return data ? [data] : [];
};

const normalizePath = (url) => {
    if (!url) return "";

    try {
        return new URL(url, window.location.origin).pathname;
    } catch (error) {
        return String(url).split("?")[0];
    }
};

const normalizeFullUrl = (url) => {
    if (!url) return "";

    try {
        const parsedUrl = new URL(url, window.location.origin);
        return `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
        return String(url);
    }
};

const isSameTargetUrl = (applUrl, currentUrl) => {
    if (!applUrl) return false;

    const applPath = normalizePath(applUrl);
    const currentPath = normalizePath(currentUrl);
    const applFullUrl = normalizeFullUrl(applUrl);
    const currentFullUrl = normalizeFullUrl(currentUrl);

    return applFullUrl === currentFullUrl || applPath === currentPath || applUrl === currentUrl;
};

const parseDateTime = (value, isEndDate = false) => {
    if (!value) return null;

    const text = String(value).trim();

    if (/^\d{8}$/.test(text)) {
        const year = Number(text.slice(0, 4));
        const month = Number(text.slice(4, 6)) - 1;
        const day = Number(text.slice(6, 8));
        return isEndDate
            ? new Date(year, month, day, 23, 59, 59, 999).getTime()
            : new Date(year, month, day).getTime();
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        const [year, month, day] = text.split("-").map(Number);
        return isEndDate
            ? new Date(year, month - 1, day, 23, 59, 59, 999).getTime()
            : new Date(year, month - 1, day).getTime();
    }

    const time = new Date(text).getTime();
    return Number.isNaN(time) ? null : time;
};

const isWithinDisplayPeriod = (metaTag) => {
    const now = Date.now();
    const beginTime = parseDateTime(metaTag.applBeginDttm);
    const endTime = parseDateTime(metaTag.applEndDttm, true);

    if (beginTime && now < beginTime) return false;
    if (endTime && now > endTime) return false;

    return true;
};

const clearManagedMetaTags = () => {
    document.head.querySelectorAll(`[${MANAGED_ATTR}="true"]`).forEach((element) => {
        element.remove();
    });
};

const applyMetaContent = (tagCntnt) => {
    clearManagedMetaTags();

    const parser = new DOMParser();
    const metaDocument = parser.parseFromString(String(tagCntnt || ""), "text/html");

    metaDocument.querySelectorAll("meta, link[rel='canonical']").forEach((element) => {
        const headElement = element.cloneNode(true);
        headElement.setAttribute(MANAGED_ATTR, "true");
        document.head.appendChild(headElement);
    });
};

const applyMetaTag = (metaTag) => {
    if (!metaTag.tagCntnt) {
        clearManagedMetaTags();
        return;
    }

    applyMetaContent(metaTag.tagCntnt);
};

const clearMetaTag = () => {
    clearManagedMetaTags();
};

const filterMetaTags = (metaTags, currentUrl) => {
    return metaTags.filter((metaTag) => {
        return (
            metaTag.tagExpsrYn !== "N" &&
            isSameTargetUrl(metaTag.tagApplUrl, currentUrl) &&
            isWithinDisplayPeriod(metaTag)
        );
    });
};

export const applyCommonMetaTag = async (currentUrl) => {
    if (typeof window === "undefined") return null;

    const requestId = latestRequestId + 1;
    latestRequestId = requestId;

    try {
        const response = await commonAPI.selectMetaTag({ tagApplUrl: currentUrl });
        if (requestId !== latestRequestId) return null;

        const metaTag = filterMetaTags(getMetaRows(response), currentUrl)[0];

        if (!metaTag) {
            clearMetaTag();
            return null;
        }

        applyMetaTag(metaTag);
        return metaTag;
    } catch (error) {
        if (requestId === latestRequestId) clearMetaTag();
        return null;
    }
};
